import React from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Calendar, Download, Wrench, List, CalendarDays } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import PageBlocks from "@/components/site/PageBlocks";
import ActionsCalendar from "@/components/site/ActionsCalendar";

const STATUS = {
  a_venir: { label: "À venir", color: "bg-cef-blue text-white" },
  en_cours: { label: "En cours", color: "bg-cef-green" },
  termine: { label: "Terminé", color: "bg-white" },
};
const TYPE = {
  evenement: Calendar,
  mobilisation: Calendar,
  outil: Wrench,
};

export default function Actions() {
  const [items, setItems] = React.useState([]);
  const [view, setView] = React.useState("list");

  React.useEffect(() => {
    api.get("/actions").then((r) => setItems(r.data));
  }, []);

  const events = items.filter((i) => i.type !== "outil");
  const tools = items.filter((i) => i.type === "outil");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <PageHeader slug="actions" fallbackTitle="Actions [&](pink) campagnes" fallbackSubtitle="Manifestations, ateliers, kits clé en main : tout pour passer à l'action." />

      <PageBlocks slug="actions" className="mt-6" />

      <section className="mt-10">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="font-display font-black text-2xl tracking-tighter uppercase">Agenda</h2>
          <div className="flex gap-2" data-testid="actions-view-toggle">
            <button
              onClick={() => setView("list")}
              className={`neo-btn text-sm py-2 ${view === "list" ? "bg-black text-white" : "bg-white"}`}
              data-testid="view-list"
            >
              <List className="w-4 h-4" /> Liste
            </button>
            <button
              onClick={() => setView("calendar")}
              className={`neo-btn text-sm py-2 ${view === "calendar" ? "bg-black text-white" : "bg-white"}`}
              data-testid="view-calendar"
            >
              <CalendarDays className="w-4 h-4" /> Calendrier
            </button>
          </div>
        </div>

        {view === "calendar" && <ActionsCalendar items={events} getHref={(it) => `/actions/${it.id}`} />}

        {view === "list" && (
          <div className="grid md:grid-cols-2 gap-5" data-testid="events-grid">
          {events.map((e) => {
            const Icon = TYPE[e.type] || Calendar;
            return (
              <Link key={e.id} to={`/actions/${e.id}`} className="neo-card p-6 block group hover:-translate-y-0.5 transition" data-testid={`event-${e.id}`}>
                <div className="flex items-center justify-between">
                  <Icon className="w-6 h-6" strokeWidth={2.5} />
                  <span className={`chip ${STATUS[e.status]?.color}`}>{STATUS[e.status]?.label}</span>
                </div>
                <h3 className="mt-3 font-display font-black text-lg tracking-tight group-hover:underline">{e.title}</h3>
                <p className="mt-2 text-sm text-neutral-600 line-clamp-3">{e.description}</p>
                <div className="mt-3 text-sm font-bold">📅 {new Date(e.date).toLocaleString("fr-BE", { dateStyle: "long" })}</div>
                <div className="text-sm">📍 {e.location}</div>
                <span className="mt-4 inline-flex text-sm font-bold underline">Voir l'action →</span>
              </Link>
            );
          })}
          </div>
        )}
      </section>

      <section className="mt-12">
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase mb-4">Kits & guides à télécharger</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="tools-grid">
          {tools.map((t) => (
            <Link key={t.id} to={`/actions/${t.id}`} className="neo-card p-5 bg-cef-yellow block group hover:-translate-y-0.5 transition" data-testid={`tool-${t.id}`}>
              <Wrench className="w-6 h-6" strokeWidth={2.5} />
              <h3 className="mt-3 font-display font-black text-lg tracking-tight group-hover:underline">{t.title}</h3>
              <p className="mt-2 text-sm line-clamp-3">{t.description}</p>
              {t.kit_url && (
                <span className="neo-btn bg-black text-white text-sm mt-3 inline-flex pointer-events-none">
                  <Download className="w-4 h-4" /> Pack inclus
                </span>
              )}
              <span className="mt-3 block text-sm font-bold underline">Voir l'outil →</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
